/**
 * Admin UI translations. English is the key and the fallback.
 * Missing keys and unknown locales render the current English UI unchanged.
 *
 * Does not translate merchant-authored or API-stored campaign/widget values.
 */

import fr from './locales/fr.json';
import de from './locales/de.json';
import es from './locales/es.json';
import it from './locales/it.json';
import nl from './locales/nl.json';
import ptBR from './locales/pt-BR.json';
import zhCN from './locales/zh-CN.json';
import ja from './locales/ja.json';

export const ADMIN_LOCALES = [
  'en',
  'fr',
  'de',
  'es',
  'it',
  'nl',
  'pt-BR',
  'zh-CN',
  'ja',
] as const;
export type AdminLocale = (typeof ADMIN_LOCALES)[number];

/** Display order and English names match the language picker UI. */
export const ADMIN_LOCALE_OPTIONS: {
  value: AdminLocale;
  label: string;
  flag: string;
  flagCode: string;
}[] = [
  { value: 'en', label: 'English', flag: '🇬🇧', flagCode: 'gb' },
  { value: 'de', label: 'German', flag: '🇩🇪', flagCode: 'de' },
  { value: 'es', label: 'Spanish', flag: '🇪🇸', flagCode: 'es' },
  { value: 'fr', label: 'French', flag: '🇫🇷', flagCode: 'fr' },
  { value: 'it', label: 'Italian', flag: '🇮🇹', flagCode: 'it' },
  { value: 'nl', label: 'Dutch', flag: '🇳🇱', flagCode: 'nl' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)', flag: '🇧🇷', flagCode: 'br' },
  { value: 'zh-CN', label: 'Chinese (Simplified)', flag: '🇨🇳', flagCode: 'cn' },
  { value: 'ja', label: 'Japanese', flag: '🇯🇵', flagCode: 'jp' },
];

const STORAGE_KEY = 'gf_locale';
const OVERRIDE_KEY = 'gf_admin_locale';

type Dict = Record<string, string>;

const DICTS: Record<AdminLocale, Dict> = {
  en: {},
  fr: fr as Dict,
  de: de as Dict,
  es: es as Dict,
  it: it as Dict,
  nl: nl as Dict,
  'pt-BR': ptBR as Dict,
  'zh-CN': zhCN as Dict,
  ja: ja as Dict,
};

function normalizeLocale(raw: string | null | undefined): AdminLocale {
  if (!raw) return 'en';
  const lower = raw.replace(/_/g, '-').toLowerCase();
  if (lower === 'pt-br' || lower === 'pt' || lower.startsWith('pt-br')) {
    return 'pt-BR';
  }
  if (lower === 'pt-pt' || lower.startsWith('pt-pt')) return 'pt-BR';
  if (lower === 'zh-cn' || lower === 'zh' || lower.startsWith('zh-cn')) {
    return 'zh-CN';
  }
  if (lower.startsWith('zh-tw') || lower.startsWith('zh-hk')) return 'en';
  const lang = lower.split('-')[0];
  if (
    lang === 'fr' ||
    lang === 'de' ||
    lang === 'es' ||
    lang === 'it' ||
    lang === 'nl' ||
    lang === 'ja'
  ) {
    return lang;
  }
  return 'en';
}

function isAdminLocale(raw: string | null | undefined): raw is AdminLocale {
  return (ADMIN_LOCALES as readonly string[]).includes(raw || '');
}

function readOverride(): AdminLocale | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(OVERRIDE_KEY);
    return isAdminLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

function readShopifyLocale(): AdminLocale {
  if (typeof window === 'undefined') return 'en';
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('locale');
    if (fromUrl) {
      const normalized = normalizeLocale(fromUrl);
      sessionStorage.setItem(STORAGE_KEY, normalized);
      return normalized;
    }
    return normalizeLocale(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return 'en';
  }
}

function readLocale(): AdminLocale {
  return readOverride() ?? readShopifyLocale();
}

let currentLocale: AdminLocale = readLocale();

export function getAdminLocale(): AdminLocale {
  return currentLocale;
}

/** Persist a merchant pick. Overrides the Shopify admin locale until changed. */
export function setAdminLocale(locale: AdminLocale): void {
  currentLocale = locale;
  try {
    localStorage.setItem(OVERRIDE_KEY, locale);
    sessionStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
}

export function interpolate(
  template: string,
  vars?: Record<string, string | number>,
): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) =>
    vars[name] == null ? `{{${name}}}` : String(vars[name]),
  );
}

/** Collapse wrap-script whitespace so multiline JSX text matches locale keys. */
export function normalizeKey(key: string): string {
  return key.replace(/\s+/g, ' ').trim();
}

/** Translate admin chrome. Returns `key` unchanged when no translation exists. */
export function t(
  key: string,
  vars?: Record<string, string | number>,
): string {
  if (!key) return key;
  const k = normalizeKey(key);
  const dict = DICTS[currentLocale] || DICTS.en;
  const translated = dict[k] ?? k;
  return interpolate(translated, vars);
}

export function isTranslatedLocale(): boolean {
  return currentLocale !== 'en';
}
